import m from "mithril";
import { getMithril, showToast, formatRupiah, exportToXLSX, toExcelNumber } from "../../../utils.js";

const CashFlow = {
    loading: false,

    // Default to current month
    startDate: (() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    })(),
    endDate: (() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    })(),

    method: 'indirect', // 'indirect' or 'direct'

    data: {
        method: 'indirect',
        operatingActivities: [],
        totalOperatingCashFlow: 0,
        investingActivities: [],
        totalInvestingCashFlow: 0,
        financingActivities: [],
        totalFinancingCashFlow: 0,
        netIncreaseCash: 0,
        beginningCash: 0,
        endingCash: 0,
        period: {
            startDate: '',
            endDate: ''
        }
    },

    oninit: function (vnode) {
        this.fetchData();
    },

    fetchData: async function () {
        this.loading = true;
        m.redraw();

        try {
            const url = `/api/accounting/reports/cash-flow?startDate=${this.startDate}&endDate=${this.endDate}&method=${this.method}`;
            const response = await getMithril(url, { component: this });

            if (response.success) {
                this.data = response.data;
            } else {
                showToast(response.message || "Gagal memuat Arus Kas", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Terjadi kesalahan saat memuat Arus Kas", "error");
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    exportCSV: function () {
        if (!this.data) {
            showToast("Tidak ada data untuk diekspor", "warning");
            return;
        }

        const headers = ["Kategori", "Keterangan", "Jumlah"];
        const rows = [];

        // Numeric cells must be JS Numbers so Excel stores them as numbers (not text).
        const toNum = (val) => toExcelNumber(val);

        // Operating Activities
        rows.push(["AKTIVITAS OPERASI", "", ""]);
        this.data.operatingActivities.forEach(acc => {
            rows.push(["Operasi", acc.name, toNum(acc.amount)]);
        });
        rows.push(["Total Arus Kas Bersih dari Aktivitas Operasi", "", toNum(this.data.totalOperatingCashFlow)]);
        rows.push(["", "", ""]);

        // Investing Activities
        rows.push(["AKTIVITAS INVESTASI", "", ""]);
        this.data.investingActivities.forEach(acc => {
            rows.push(["Investasi", acc.name, toNum(acc.amount)]);
        });
        rows.push(["Total Arus Kas Bersih dari Aktivitas Investasi", "", toNum(this.data.totalInvestingCashFlow)]);
        rows.push(["", "", ""]);

        // Financing Activities
        rows.push(["AKTIVITAS PENDANAAN", "", ""]);
        this.data.financingActivities.forEach(acc => {
            rows.push(["Pendanaan", acc.name, toNum(acc.amount)]);
        });
        rows.push(["Total Arus Kas Bersih dari Aktivitas Pendanaan", "", toNum(this.data.totalFinancingCashFlow)]);
        rows.push(["", "", ""]);

        // Summary
        rows.push(["Kenaikan (Penurunan) Kas Bersih", "", toNum(this.data.netIncreaseCash)]);
        rows.push(["Saldo Kas Awal Periode", "", toNum(this.data.beginningCash)]);
        rows.push(["SALDO KAS AKHIR PERIODE", "", toNum(this.data.endingCash)]);

        exportToXLSX(`Arus_Kas_${this.method}_${this.startDate}_sd_${this.endDate}`, headers, rows);
    },

    renderActivityRows: function (activities) {
        if (!activities || activities.length === 0) {
            return m("tr", [
                m("td", { class: "px-6 py-4 text-sm text-gray-500 italic pl-12" }, "Tidak ada aktivitas pada periode ini"),
                m("td", { class: "px-6 py-4 text-sm text-gray-900 text-right font-mono" }, "-")
            ]);
        }

        return activities.map(acc => m("tr", { class: `border-b border-gray-100 hover:bg-gray-50/50 ${acc.isBase ? 'bg-indigo-50/10' : ''}` }, [
            m("td", { class: `px-6 py-2 text-sm text-gray-700 ${acc.isBase ? 'font-semibold' : 'pl-12'}` },
                acc.name
            ),
            m("td", { class: "px-6 py-2 text-sm text-gray-900 text-right font-mono" },
                formatRupiah(acc.amount)
            )
        ]));
    },

    view: function (vnode) {
        return m("div", { class: "container mx-auto px-4 py-6 max-w-5xl animate-fade-in" }, [

            // Header Section
            m("div", { class: "mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                m("div", { class: "flex flex-col md:flex-row md:items-center justify-between gap-4" }, [
                    m("div", [
                        m("h1", { class: "text-2xl font-bold tracking-tight text-gray-900" }, "Laporan Arus Kas"),
                        m("p", { class: "mt-1 text-sm text-gray-500" }, "Statement of Cash Flows")
                    ]),

                    m("div", { class: "flex flex-col items-end gap-3" }, [
                        // Method Selector
                        m("div", { class: "flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-200" }, [
                            m("button", {
                                class: `px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${this.method === 'indirect' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`,
                                onclick: () => {
                                    this.method = 'indirect';
                                    this.fetchData();
                                }
                            }, "Metode Tidak Langsung"),
                            m("button", {
                                class: `px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${this.method === 'direct' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`,
                                onclick: () => {
                                    this.method = 'direct';
                                    this.fetchData();
                                }
                            }, "Metode Langsung"),
                        ]),

                        // Date Pickers + Export
                        m("div", { class: "flex flex-wrap items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-100" }, [
                            m("div", { class: "flex items-center gap-2" }, [
                                m("label", { class: "text-xs font-medium text-gray-500 ml-1" }, "Dari:"),
                                m("input", {
                                    type: "date",
                                    class: "px-3 py-1.5 bg-white text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                                    value: this.startDate,
                                    onchange: (e) => {
                                        this.startDate = e.target.value;
                                        this.fetchData();
                                    }
                                })
                            ]),
                            m("span", { class: "text-gray-400" }, "-"),
                            m("div", { class: "flex items-center gap-2" }, [
                                m("label", { class: "text-xs font-medium text-gray-500 ml-1" }, "Sampai:"),
                                m("input", {
                                    type: "date",
                                    class: "px-3 py-1.5 bg-white text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                                    value: this.endDate,
                                    onchange: (e) => {
                                        this.endDate = e.target.value;
                                        this.fetchData();
                                    }
                                })
                            ]),
                            m("button", {
                                class: "inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors ml-2",
                                onclick: () => this.exportCSV(),
                                title: "Ekspor ke CSV"
                            }, [
                                m("i", { class: "ri-file-download-line mr-2" }),
                                "Ekspor XLSX"
                            ])
                        ])
                    ])
                ])
            ]),

            // Main Content Area
            this.loading
                ? m("div", { class: "flex justify-center items-center py-20" }, [
                    m("div", { class: "animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" })
                ])
                : m("div", { class: "bg-white shadow-md rounded-xl border border-gray-200 overflow-hidden" }, [

                    // Report Header
                    m("div", { class: "text-center py-8 border-b border-gray-200 bg-gray-50/50" }, [
                        m("h2", { class: "text-2xl font-bold text-gray-900 tracking-tight" }, "LAPORAN ARUS KAS"),
                        m("p", { class: "text-sm text-gray-600 mt-2 font-medium" },
                            `Metode ${this.data.method === 'direct' ? 'Langsung' : 'Tidak Langsung'} • Periode: ${new Date(this.data.period.startDate).toLocaleDateString()} s/d ${new Date(this.data.period.endDate).toLocaleDateString()}`
                        )
                    ]),

                    // Content Section
                    m("div", { class: "p-0" }, [
                        m("table", { class: "min-w-full divide-y divide-gray-200" }, [
                            m("tbody", { class: "bg-white divide-y divide-gray-100" }, [

                                // 1. Operating Activities
                                m("tr", { class: "bg-emerald-50/40" }, [
                                    m("td", { class: "px-6 py-4 text-base font-bold text-emerald-900 uppercase", colspan: "2" }, "Aktivitas Operasi")
                                ]),
                                this.renderActivityRows(this.data.operatingActivities),
                                m("tr", { class: "bg-gray-50/60 font-semibold" }, [
                                    m("td", { class: "px-6 py-3 text-sm text-gray-800" }, "Arus Kas Bersih dari Aktivitas Operasi"),
                                    m("td", { class: "px-6 py-3 text-sm text-gray-900 text-right font-mono border-t border-gray-200" }, formatRupiah(this.data.totalOperatingCashFlow))
                                ]),

                                // 2. Investing Activities
                                m("tr", { class: "bg-blue-50/40" }, [
                                    m("td", { class: "px-6 py-4 text-base font-bold text-blue-900 uppercase", colspan: "2" }, "Aktivitas Investasi")
                                ]),
                                this.renderActivityRows(this.data.investingActivities),
                                m("tr", { class: "bg-gray-50/60 font-semibold" }, [
                                    m("td", { class: "px-6 py-3 text-sm text-gray-800" }, "Arus Kas Bersih dari Aktivitas Investasi"),
                                    m("td", { class: "px-6 py-3 text-sm text-gray-900 text-right font-mono border-t border-gray-200" }, formatRupiah(this.data.totalInvestingCashFlow))
                                ]),

                                // 3. Financing Activities
                                m("tr", { class: "bg-purple-50/40" }, [
                                    m("td", { class: "px-6 py-4 text-base font-bold text-purple-900 uppercase", colspan: "2" }, "Aktivitas Pendanaan")
                                ]),
                                this.renderActivityRows(this.data.financingActivities),
                                m("tr", { class: "bg-gray-50/60 font-semibold" }, [
                                    m("td", { class: "px-6 py-3 text-sm text-gray-800" }, "Arus Kas Bersih dari Aktivitas Pendanaan"),
                                    m("td", { class: "px-6 py-3 text-sm text-gray-900 text-right font-mono border-t border-gray-200" }, formatRupiah(this.data.totalFinancingCashFlow))
                                ]),

                            ])
                        ])
                    ]),

                    // Final Totals Section
                    m("div", { class: "bg-gray-50 border-t border-gray-200 p-0" }, [
                        m("table", { class: "min-w-full" }, [
                            m("tbody", { class: "divide-y divide-gray-200" }, [

                                // Net Increase in Cash
                                m("tr", { class: "bg-white" }, [
                                    m("td", { class: "px-6 py-4 text-sm font-bold text-gray-900" }, "Kenaikan (Penurunan) Kas Bersih"),
                                    m("td", { class: `px-6 py-4 text-lg font-bold text-right font-mono ${this.data.netIncreaseCash >= 0 ? 'text-green-600' : 'text-red-600'}` },
                                        formatRupiah(this.data.netIncreaseCash)
                                    )
                                ]),

                                // Beginning Cash
                                m("tr", { class: "bg-gray-50" }, [
                                    m("td", { class: "px-6 py-4 text-sm font-semibold text-gray-700" }, "Saldo Kas Awal Periode"),
                                    m("td", { class: "px-6 py-4 text-base font-bold text-gray-900 text-right font-mono" },
                                        formatRupiah(this.data.beginningCash)
                                    )
                                ]),

                                // Ending Cash
                                m("tr", { class: "bg-emerald-50 border-t-2 border-emerald-200" }, [
                                    m("td", { class: "px-6 py-6 text-lg font-bold text-emerald-900 uppercase tracking-wide" }, "Saldo Kas Akhir Periode"),
                                    m("td", { class: "px-6 py-6 text-2xl font-bold text-emerald-700 text-right font-mono" },
                                        formatRupiah(this.data.endingCash)
                                    )
                                ])

                            ])
                        ])
                    ])
                ])
        ]);
    }
};

export default CashFlow;
